/**
 * Data layer for `advertiser-api`. Thin typed fetch wrappers over /api/v1/advertiser-api.
 * Raw `fetch()` is confined to this file per the dash contract.
 *
 * Wave 7 domain: advertiser API — issue opaque keys w/ scoped permissions so
 * advertisers can POST conversions and GET /reports/summary from their
 * backends. This file only handles client-side token generation (preview) +
 * list/revoke via the API.
 */

export type AdvertiserApiScope =
	| "conversions:write"
	| "conversions:read"
	| "reports:read";

export const ADVERTISER_API_SCOPES: ReadonlyArray<{
	id: AdvertiserApiScope;
	label: string;
	description: string;
}> = [
	{
		id: "conversions:write",
		label: "conversions:write",
		description: "POST /api/v1/advertiser-api/conversions — push new conversions.",
	},
	{
		id: "conversions:read",
		label: "conversions:read",
		description: "GET /api/v1/advertiser-api/conversions — list pushed conversions.",
	},
	{
		id: "reports:read",
		label: "reports:read",
		description: "GET /api/v1/advertiser-api/reports/summary — totals & rollups.",
	},
];

export interface AdvertiserApiKeyRow {
	id: string;
	tenant_id: string;
	advertiser_id: string;
	label: string | null;
	status: "active" | "revoked";
	scopes: AdvertiserApiScope[];
	last_used_at: number | null;
	created_at: number;
	updated_at: number;
}

export interface ListAdvertiserApiKeysResponse {
	count: number;
	items: AdvertiserApiKeyRow[];
}

export interface CreateAdvertiserApiKeyBody {
	label?: string;
	advertiser_id?: string;
	scopes?: AdvertiserApiScope[];
}

export interface CreateAdvertiserApiKeyResponse extends AdvertiserApiKeyRow {
	api_key: string; // one-time reveal
}

const BASE = "/api/v1/advertiser-api";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListAdvertiserApiKeysResponse> {
	const res = await fetch(`${BASE}/keys`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListAdvertiserApiKeysResponse>(res);
}

export async function create(
	body: CreateAdvertiserApiKeyBody,
): Promise<CreateAdvertiserApiKeyResponse> {
	const res = await fetch(`${BASE}/keys`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<CreateAdvertiserApiKeyResponse>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/keys/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const advertiserApiQueryKeys = {
	all: ["advertiser-api"] as const,
	list: () => [...advertiserApiQueryKeys.all] as const,
	detail: (id: string) => [...advertiserApiQueryKeys.all, id] as const,
};

// -- Pure helpers (vitest) --------------------------------------------------

/**
 * Generate a client-side preview token. The real key is always issued by the
 * server; this is only a UX helper so the user can see what the key format
 * will look like before they click "Issue". Not cryptographic.
 *
 * Format: `adv_<24-url-safe-chars>` (prefix makes secrets greppable in logs).
 */
export function generatePreviewToken(
	randomBytes: (n: number) => Uint8Array = defaultRandomBytes,
): string {
	const bytes = randomBytes(18);
	// URL-safe base64 → alphanum + - _
	const b64 = bytesToUrlSafeBase64(bytes);
	return `adv_${b64.slice(0, 24)}`;
}

function defaultRandomBytes(n: number): Uint8Array {
	const out = new Uint8Array(n);
	if (
		typeof globalThis.crypto !== "undefined" &&
		typeof globalThis.crypto.getRandomValues === "function"
	) {
		globalThis.crypto.getRandomValues(out);
	} else {
		for (let i = 0; i < n; i++) out[i] = Math.floor(Math.random() * 256);
	}
	return out;
}

function bytesToUrlSafeBase64(bytes: Uint8Array): string {
	let bin = "";
	for (let i = 0; i < bytes.length; i++) {
		bin += String.fromCharCode(bytes[i]!);
	}
	// btoa is available in jsdom + browser + workerd (all dash targets)
	const b64 = btoa(bin);
	return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

/**
 * Toggle a scope within a selected-scopes set. Pure function — no React.
 * Preserves the canonical scope order from ADVERTISER_API_SCOPES.
 */
export function toggleScope(
	current: ReadonlyArray<AdvertiserApiScope>,
	scope: AdvertiserApiScope,
): AdvertiserApiScope[] {
	const has = current.includes(scope);
	const next = has
		? current.filter((s) => s !== scope)
		: [...current, scope];
	// Re-sort to canonical order for stable API payloads.
	const order = ADVERTISER_API_SCOPES.map((s) => s.id);
	return [...next].sort(
		(a, b) => order.indexOf(a) - order.indexOf(b),
	);
}

/**
 * Validate a scope array against the known scope catalog. Rejects duplicates
 * and unknown values. Returns a canonical sorted array on success.
 */
export function validateScopes(
	raw: ReadonlyArray<string>,
): { ok: true; value: AdvertiserApiScope[] } | { ok: false; error: string } {
	const known = new Set<string>(ADVERTISER_API_SCOPES.map((s) => s.id));
	const seen = new Set<string>();
	const out: AdvertiserApiScope[] = [];
	for (const s of raw) {
		if (!known.has(s)) return { ok: false, error: `unknown_scope:${s}` };
		if (seen.has(s)) return { ok: false, error: `duplicate_scope:${s}` };
		seen.add(s);
		out.push(s as AdvertiserApiScope);
	}
	const order = ADVERTISER_API_SCOPES.map((s) => s.id);
	out.sort((a, b) => order.indexOf(a) - order.indexOf(b));
	return { ok: true, value: out };
}

/**
 * Mask a revealed key for inline display — shows the prefix + last 4 chars.
 * Used in the one-time-reveal card to prompt the user to copy.
 */
export function maskKey(key: string): string {
	if (!key) return "";
	if (key.length <= 10) return key.replace(/./g, "*");
	const head = key.slice(0, 4);
	const tail = key.slice(-4);
	return `${head}${"*".repeat(Math.max(8, key.length - 8))}${tail}`;
}
